// Per-platform stored-wallet capability gate — WEB fork of
// useWalletCapability.ts. CPS-26590 Task 7: the web wallet sheet (
// AdyenCardAddSheet.web.tsx) now mounts real adyen-web ApplePay/GooglePay
// components, so this gate is live.
//
// Apple Pay: real, cheap, synchronous browser API — `ApplePaySession` only
// exists in Safari, and `canMakePayments()` reflects actual device/wallet
// capability. This is directly comparable to the native hook's device-class
// proxy (Platform.OS === 'ios'), just backed by a real feature check instead
// of a platform check (a Safari-only global exists here, unlike native).
//
// Google Pay: there is no comparable lightweight, pre-mount readiness check.
// The real one — GooglePay#isAvailable(), backed by the Google Pay JS SDK's
// isReadyToPay() — needs a live AdyenCheckout instance (built from the
// merchant's /paymentMethods googlepay entry), which doesn't exist yet at
// this row-visibility gate (discovery only runs once the sheet opens — same
// "pre-discovery" reasoning the native hook documents for its own device-
// capability proxies). Loading the adyen-web SDK just to answer "should this
// row render" would also undermine the whole point of lazy-loading it. So
// this stays `true` — broadly available, matching every modern Chromium-based
// browser — and the finer-grained "is Google Pay actually usable here" check
// is deferred to AdyenCardAddSheet.web's mountAdyenWebComponent, which calls
// the real isAvailable() before mount and fails gracefully to the sheet's
// terminal error state (never a dead host) if it's not.
//
// Reads off `globalThis` rather than `window` so this stays safe to import
// under the node jest test environment (no `window` global there) — the same
// boundary convention as utils/adyenRedirectSession.web.ts. In a real browser
// `window === globalThis`, so this is behaviorally identical to a
// `window.ApplePaySession` check.

export type WalletCapability = {
  applePay: boolean;
  googlePay: boolean;
};

// Structural shape of the standard ApplePaySession global, declared locally
// rather than relying on @adyen/adyen-web's ambient ApplePayJS types being
// loaded elsewhere in the program — this file has no dependency on the SDK.
type ApplePaySessionLike = {
  canMakePayments: () => boolean;
};

const getApplePaySession = (): ApplePaySessionLike | undefined =>
  (globalThis as { ApplePaySession?: ApplePaySessionLike }).ApplePaySession;

// canMakePayments() is a real browser API call and can throw in some
// contexts (e.g. an insecure/sandboxed origin) — guard defensively so a throw
// here degrades to "unavailable" rather than crashing the row-visibility
// gate.
const isApplePayCapable = (): boolean => {
  const session = getApplePaySession();
  if (!session) {
    return false;
  }
  try {
    return session.canMakePayments();
  } catch {
    return false;
  }
};

const useWalletCapability = (): WalletCapability => ({
  applePay: isApplePayCapable(),
  googlePay: true,
});

export default useWalletCapability;
